import React from 'react'
import { Link } from 'react-router-dom'
import { useSEO } from '../hooks/useSEO'
import { VideoPlayer } from '../components/VideoPlayer'
import { ContactForm } from '../components/ContactForm'
import { useCMSCollection, useCMSSide } from '../hooks/useCMS'

const FALLBACK_VIDEOS = [
  {
    badge: 'Bo Steendahl · Forandring indefra',
    title: 'BoSteendahlDK profilfilm 24',
    description: 'Hør Bo fortælle om sin tilgang og om, hvordan kropsterapi, coaching og hypnoterapi arbejder sammen for at skabe ro, styrke og balance.',
    type: 'youtube',
    source: '29PQYHSfCTA',
    poster: 'https://i.ytimg.com/vi/29PQYHSfCTA/hqdefault.jpg',
  },
  {
    badge: 'Klientfortælling',
    title: "Hør hele Claus' fortælling",
    description: "Hør hele Claus' fortælling om sin vilde forvandling, fra stress, hårtab og indre kaos – til dyb ro, styrke og selvkontrol.",
    type: 'file',
    source: '/_assets/claus-teaser-fb-opslag.mp4',
    poster: '/_assets/20220903-135741.jpg',
  },
  {
    badge: 'Gode råd til hverdagen',
    title: 'Slip af med din hovedpine',
    description: 'Enkle og effektive trykpunkter fra kropsterapien, der kan hjælpe dig med at lindre spændinger i hoved, nakke og skuldre.',
    type: 'file',
    source: '/_assets/hovedpine-trykpunkter.mp4',
    poster: '/_assets/20241001-091341638-ios.png',
  },
]

export function VideoPage() {
  const { data: cmsVideos } = useCMSCollection('videoer')
  const { side: cmsSide } = useCMSSide('video')

  const videos = Array.isArray(cmsVideos) && cmsVideos.length > 0
    ? [...cmsVideos]
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((v) => {
          const isFile =
            v.terms?.videotype?.includes('fil') ||
            v.video_type === 'file' ||
            v.media_source?.includes('.mp4') ||
            v.media_source?.startsWith('/_assets/')
          const poster = Array.isArray(v.poster) ? v.poster[0] : v.poster
          return {
            badge: v.title?.toLowerCase().includes('claus')
              ? 'Klientfortælling'
              : v.title?.toLowerCase().includes('hovedpine')
              ? 'Gode råd til hverdagen'
              : 'Bo Steendahl · Forandring indefra',
            title: v.title,
            description: v.description,
            type: isFile ? 'file' : 'youtube',
            source: v.media_source,
            poster: poster,
          }
        })
    : FALLBACK_VIDEOS

  useSEO({
    title: cmsSide?.seo_titel || 'VIDEO / LYD-fil | Trekantens Kropsterapi',
    description: cmsSide?.seo_beskrivelse || 'Se videoer og lyt til fortællinger med Bo Steendahl. Profilfilm, Claus\' forvandling og trykpunkter mod hovedpine.',
    ogImage: cmsSide?.dele_billede?.[0] || 'https://assets.auxodev.dk/assets/2a253654-8b0d-44c3-813e-14453c64ff7c/logo-11-dec.png',
  })

  return (
    <div className="bg-[#FAF8F5]">
      {/* Hero Section */}
      <section className="relative py-16 lg:py-24 bg-gradient-to-b from-[#F5EFE9] to-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#1b3a4b]/8 text-[#1b3a4b] text-xs font-semibold uppercase tracking-wider mb-4">
            <span className="w-1.5 h-1.5 rounded-full bg-[#7c875a]" />
            Videoer &amp; Fortællinger
          </div>
          <h1 className="font-serif text-[32px] sm:text-5xl lg:text-6xl font-bold text-[#1b3a4b] tracking-tight">
            {cmsSide?.hero_overskrift || 'VIDEO / LYD-fil'}
          </h1>
          <p className="mt-4 text-lg sm:text-xl font-serif text-[#1b3a4b]/90 max-w-2xl mx-auto leading-relaxed">
            {cmsSide?.hero_underoverskrift || 'Hør historierne og få et levende indblik i forandringen indefra'}
          </p>
        </div>
      </section>

      {/* Videos Section with Aside Contact Form */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="lg:col-span-7 xl:col-span-8 space-y-12">
              {videos.map((vid, idx) => (
                <div key={idx} className="bg-white rounded-3xl p-8 sm:p-10 shadow-sm border border-[#E5E0D8] space-y-6">
                  <div>
                    <span className="text-xs font-semibold text-[#7c875a] uppercase tracking-wider block mb-1">
                      {vid.badge}
                    </span>
                    <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#1b3a4b]">
                      {vid.title}
                    </h2>
                    {vid.description && (
                      <p className="mt-2 text-sm text-[#475569] leading-relaxed">
                        {vid.description}
                      </p>
                    )}
                  </div>
                  <VideoPlayer
                    type={vid.type}
                    source={vid.source}
                    poster={vid.poster}
                    title={vid.title}
                    description={vid.description}
                  />
                </div>
              ))}
            </div>

            {/* Aside: Quick Contact & Help Box */}
            <aside className="lg:col-span-5 xl:col-span-4 lg:sticky lg:top-28 space-y-6">
              <div className="bg-[#172e3b] text-[#FAF8F5] rounded-3xl p-8 shadow-lg border border-white/10">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7c875a]/20 text-[#7c875a] text-xs font-semibold mb-4">
                  <i className="fa-solid fa-calendar-check" aria-hidden="true" />
                  <span>Klar til forandring?</span>
                </div>
                <h3 className="font-serif text-2xl font-bold text-white">
                  Start din egen rejse i dag
                </h3>
                <p className="mt-3 text-sm text-[#FAF8F5]/80 leading-relaxed">
                  Uanset om du søger hjælp til stress, spændinger, uro eller gamle mønstre, er du velkommen til at kontakte mig for en uforpligtende samtale om et forløb.
                </p>
                <div className="mt-6 pt-6 border-t border-white/10">
                  <ContactForm
                    formTitle="Skriv til Bo Steendahl"
                    compact={true}
                  />
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>
    </div>
  )
}
